import { queryOptions } from '@tanstack/react-query';
import { api, apiMutation, mutation } from '../../../api/http';

export interface QuizRuleSubmission {
  tagId: string;
  count: number;
  difficultyMin: number | null;
  difficultyMax: number | null;
}

export interface QuizSetSubmission {
  slug: string;
  name: string;
  description: string;
  status: 'Draft' | 'Published' | 'Archived';
  shuffleOptions: boolean;
  isDefault: boolean;
  rules: QuizRuleSubmission[];
}

export interface QuizSetResponse {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  status: 'Draft' | 'Published' | 'Archived';
  shuffleOptions: boolean;
  isDefault: boolean;
  version: number;
  targetCount: number;
  rules: {
    id: string;
    tagId: string;
    count: number;
    difficultyMin: number | null;
    difficultyMax: number | null;
    sortOrder: number;
  }[];
}

export interface PublicQuizSetResponse {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  targetCount: number;
  shuffleOptions: boolean;
  isDefault: boolean;
}

export interface QuizStockWarning {
  ruleId: string;
  tagId: string;
  requested: number;
  allocated: number;
}

export interface QuizQuestionResponse {
  questionId: string;
  questionVersion: number;
  stem: string;
  codeSnippet: string | null;
  codeLanguage: string | null;
  options: { optionId: string; text: string }[];
}

export interface QuizDrawResponse {
  quizSetId: string;
  drawReceipt: string;
  targetCount: number;
  actualCount: number;
  shuffleOptions: boolean;
  warnings: QuizStockWarning[];
  items: QuizQuestionResponse[];
}

export interface QuizGradeSubmission {
  schemaVersion: 1;
  submissionId: string;
  quizSetId: string;
  drawReceipt: string;
  retryKind: 'none' | 'all' | 'wrong';
  parentSubmissionId: string | null;
  durationSeconds: number;
  items: { questionId: string; questionVersion: number; optionId: string | null }[];
}

export interface QuizGradeResponse {
  submissionId: string;
  total: number;
  correct: number;
  wrong: number;
  unanswered: number;
  scorePct: number;
  replayed: boolean;
  items: {
    questionId: string;
    questionVersion: number;
    result: 'Correct' | 'Wrong' | 'Unanswered';
    selectedOptionId: string | null;
    correctOptionId: string;
    explanation: string | null;
    articles: { id: string; slug: string; title: string }[];
  }[];
}

export const quizQueryKeys = {
  internalSetList: (page: number) => ['internal', 'quiz-sets', { page }] as const,
  internalSet: (id: string) => ['internal', 'quiz-sets', id] as const,
  internalSets: ['internal', 'quiz-sets'] as const,
  publicSetList: (page: number) => ['public', 'quiz-sets', { page }] as const,
  publicSet: (slug: string) => ['public', 'quiz-sets', slug] as const,
  result: (submissionId: string) => ['public', 'quiz-result', submissionId] as const,
};

export const quizSetListQuery = (page: number) =>
  queryOptions({
    queryKey: quizQueryKeys.internalSetList(page),
    queryFn: ({ signal }) =>
      api<{
        items: QuizSetResponse[];
        page: number;
        pageSize: number;
        total: number;
      }>(`/internal/quiz-sets?page=${page}&pageSize=20`, { signal }),
  });

export const quizSetQuery = (id: string) =>
  queryOptions({
    queryKey: quizQueryKeys.internalSet(id),
    queryFn: ({ signal }) => api<QuizSetResponse>(`/internal/quiz-sets/${id}`, { signal }),
  });

export function createQuizSet(submission: QuizSetSubmission) {
  return mutation<QuizSetResponse>('/internal/quiz-sets', submission);
}

export function saveQuizSet(
  id: string,
  submission: QuizSetSubmission & { expectedVersion: number },
) {
  return apiMutation<QuizSetResponse>(`/internal/quiz-sets/${id}`, 'PUT', submission);
}

export function previewQuizSet(id: string) {
  return api<QuizDrawResponse>(`/internal/quiz-sets/${id}/preview`, { cache: 'no-store' });
}

export const publicQuizSetsQuery = (page: number) =>
  queryOptions({
    queryKey: quizQueryKeys.publicSetList(page),
    queryFn: ({ signal }) =>
      api<{ items: PublicQuizSetResponse[]; page: number; pageSize: number; total: number }>(
        `/quiz/sets?page=${page}&pageSize=20`,
        { signal },
      ),
  });

export const publicQuizSetQuery = (slug: string) =>
  queryOptions({
    queryKey: quizQueryKeys.publicSet(slug),
    queryFn: ({ signal }) =>
      api<
        PublicQuizSetResponse & {
          actualCount: number;
          warnings: QuizStockWarning[];
        }
      >(`/quiz/sets/${encodeURIComponent(slug)}`, { signal }),
  });

export function drawQuiz(slug: string) {
  return api<QuizDrawResponse>(`/quiz/sets/${encodeURIComponent(slug)}/draw`, {
    cache: 'no-store',
  });
}

export function recordQuizStart(quizSetId: string, attemptId: string) {
  return api<void>('/events', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      eventId: crypto.randomUUID(),
      type: 'quiz_start',
      quizSetId,
      attemptId,
    }),
    keepalive: true,
  });
}

export function gradeQuiz(submission: QuizGradeSubmission) {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 20_000);
  return api<QuizGradeResponse>('/quiz/grade', {
    method: 'POST',
    signal: controller.signal,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(submission),
  }).finally(() => window.clearTimeout(timeout));
}

export const quizResultQuery = (submission: QuizGradeSubmission) =>
  queryOptions({
    queryKey: quizQueryKeys.result(submission.submissionId),
    queryFn: () => gradeQuiz(submission),
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
